export type PlanLimitErrorBody = {
  code?: string;
  capability?: string;
  limit?: number;
  current?: number;
  accessState?: string;
  error?: string;
  message?: string;
};

export function formatLeadLimitErrorMessage(
  limit: number,
  current: number,
  addCount: number,
  isFreePlan: boolean
): string {
  const remaining = Math.max(0, limit - current);
  const planLabel = isFreePlan ? 'free plan' : 'plan';

  if (addCount > 1) {
    if (remaining === 0) {
      return `Your ${planLabel} allows up to ${limit} leads and you already have ${current}. This import has ${addCount} leads, so none can be added. Remove leads from your file or upgrade from Billing settings to import more.`;
    }
    return `Your ${planLabel} allows up to ${limit} leads. You currently have ${current}, so you can add ${remaining} more. This import has ${addCount} leads — import ${remaining} or fewer, or upgrade from Billing settings.`;
  }

  if (remaining === 0) {
    return `Your ${planLabel} allows up to ${limit} leads and you already have ${current}. Upgrade from Billing settings to add more.`;
  }

  return `Your ${planLabel} allows up to ${limit} leads. You currently have ${current} and can add ${remaining} more. Upgrade from Billing settings for a higher limit.`;
}

export function formatStudentLimitErrorMessage(
  limit: number,
  current: number,
  addCount: number,
  isFreePlan: boolean
): string {
  const remaining = Math.max(0, limit - current);
  const planLabel = isFreePlan ? 'free plan' : 'plan';

  if (addCount > 1) {
    if (remaining === 0) {
      return `Your ${planLabel} allows up to ${limit} students and you already have ${current}. This import has ${addCount} students, so none can be added. Remove students from your file or upgrade from Billing settings to import more.`;
    }
    return `Your ${planLabel} allows up to ${limit} students. You currently have ${current}, so you can add ${remaining} more. This import has ${addCount} students — import ${remaining} or fewer, or upgrade from Billing settings.`;
  }

  if (remaining === 0) {
    return `Your ${planLabel} allows up to ${limit} students and you already have ${current}. Upgrade from Billing settings to add more.`;
  }

  return `Your ${planLabel} allows up to ${limit} students. You currently have ${current} and can add ${remaining} more. Upgrade from Billing settings for a higher limit.`;
}

export function isLeadPlanLimitError(data: PlanLimitErrorBody): boolean {
  return data.code === 'plan_limit_reached' && data.capability === 'maxLeads';
}

export function isStudentPlanLimitError(data: PlanLimitErrorBody): boolean {
  return data.code === 'plan_limit_reached' && data.capability === 'maxStudents';
}

export async function parsePlanLimitError(
  response: Response,
  addCount: number
): Promise<{ title: string; message: string } | null> {
  if (response.status !== 403) return null;

  const errorData: PlanLimitErrorBody = await response.json().catch(() => ({}));
  const isFreePlan = errorData.accessState === 'free';

  if (
    isLeadPlanLimitError(errorData) &&
    typeof errorData.limit === 'number' &&
    typeof errorData.current === 'number'
  ) {
    return {
      title: 'Lead Limit Reached',
      message: formatLeadLimitErrorMessage(
        errorData.limit,
        errorData.current,
        addCount,
        isFreePlan
      ),
    };
  }

  if (
    isStudentPlanLimitError(errorData) &&
    typeof errorData.limit === 'number' &&
    typeof errorData.current === 'number'
  ) {
    return {
      title: 'Student Limit Reached',
      message: formatStudentLimitErrorMessage(
        errorData.limit,
        errorData.current,
        addCount,
        isFreePlan
      ),
    };
  }

  if (errorData.error || errorData.message) {
    return {
      title: 'Plan Limit Reached',
      message: errorData.error || errorData.message || 'You have reached your plan limit.',
    };
  }

  return null;
}

/** @deprecated Use parsePlanLimitError instead */
export async function getLeadLimitErrorMessage(
  response: Response,
  addCount: number
): Promise<string | null> {
  const parsed = await parsePlanLimitError(response, addCount);
  return parsed?.message ?? null;
}
